import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Link,
  Checkbox,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { Subject } from '../types';
import { subjectService } from '../services/subjectService';
import { useAppStore } from '../stores/useAppStore';
import { canManageSubjects, isAdmin, isApprover } from '../utils/rbac';
import { logger } from '../utils/logger';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';
import { ExportDialog } from './ExportDialog';
import { type ExportColumn } from '../services/exportService';
import { isStudent } from '../utils/rbac';

interface SubjectListProps {
  onEdit: (subject: Subject) => void;
  onAdd: () => void;
  refreshTrigger?: number;
}

export const SubjectList: React.FC<SubjectListProps> = ({ onEdit, onAdd, refreshTrigger }) => {
  const navigate = useNavigate();
  const { user } = useAppStore();
  const userRoles = user?.roles || [];
  const canDelete = canManageSubjects(userRoles);
  const isAdminRole = isAdmin(userRoles);
  
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState<Subject | null>(null);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const canExport = !isStudent(userRoles);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load subjects
  useEffect(() => {
    loadSubjects();
  }, [debouncedSearchTerm, refreshTrigger, page]);

  // Reset page when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const loadSubjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await subjectService.getSubjects({
        page,
        pageSize,
        search: debouncedSearchTerm.trim() || undefined
      });
      
      setSubjects(result.subjects);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subjects');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (subject: Subject) => {
    setSubjectToDelete(subject);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!subjectToDelete) return;

    try {
      const lessonPlanCount = subjectToDelete.lessonPlanCount || 0;
      
      // Check if subject has lesson plans and user is not Admin
      if (lessonPlanCount > 0 && !isAdminRole) {
        showError(`Không thể xóa môn học này vì có ${lessonPlanCount} giáo án đang sử dụng môn học này. Vui lòng chuyển các giáo án sang môn học khác trước khi xóa.`);
        setDeleteDialogOpen(false);
        setSubjectToDelete(null);
        return;
      }

      await subjectService.deleteSubject(subjectToDelete.id);
      setDeleteDialogOpen(false);
      setSubjectToDelete(null);
      showSuccess('Xóa môn học thành công');
      await loadSubjects();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete subject';
      setError(errorMessage);
      showError(errorMessage);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSubjectToDelete(null);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Select all subjects (no restriction)
      setSelectedSubjectIds(subjects.map(sub => sub.id));
    } else {
      setSelectedSubjectIds([]);
    }
  };

  const handleSelectSubject = (subjectId: string, lessonPlanCount: number) => {
    logger.log('🔍 SubjectList - handleSelectSubject:', { subjectId, lessonPlanCount });
    
    setSelectedSubjectIds(prev => {
      const newIds = prev.includes(subjectId)
        ? prev.filter(id => id !== subjectId)
        : [...prev, subjectId];
      logger.log('🔍 SubjectList - Selected subject IDs:', newIds);
      return newIds;
    });
  };

  const handleBulkDeleteClick = () => {
    if (selectedSubjectIds.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      if (!isAdminRole) {
        // For non-Admin users, check if any subject has lesson plans
        const subjectsWithLessonPlans = selectedSubjectIds.filter(id => {
          const subject = subjects.find(sub => sub.id === id);
          return subject && (subject.lessonPlanCount || 0) > 0;
        });

        if (subjectsWithLessonPlans.length > 0) {
          const subjectNames = subjectsWithLessonPlans.map(id => {
            const sub = subjects.find(s => s.id === id);
            return sub?.name || id;
          }).join(', ');
          
          showError(`Không thể xóa các môn học: ${subjectNames}. Các môn học này đang có giáo án. Vui lòng chuyển các giáo án sang môn học khác trước khi xóa.`);
          setBulkDeleteDialogOpen(false);
          setSelectedSubjectIds([]);
          return;
        }
      }

      // Delete all selected subjects (Admin can delete even with lesson plans, backend will handle)
      const deletePromises = selectedSubjectIds.map(id => subjectService.deleteSubject(id));
      await Promise.all(deletePromises);
      
      showSuccess(`Đã xóa ${selectedSubjectIds.length} môn học thành công`);
      setBulkDeleteDialogOpen(false);
      setSelectedSubjectIds([]);
      await loadSubjects();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Không thể xóa môn học');
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteDialogOpen(false);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('vi-VN');
    } catch (error) {
      return '-';
    }
  };

  const handleExportSubjects = async (dateFrom: Date | null, dateTo: Date | null): Promise<Subject[]> => {
    // Subjects don't need date range, but we keep the signature for consistency
    const result = await subjectService.getSubjects({ page: 1, pageSize: 10000 });
    return result.subjects;
  };

  const subjectExportColumns: ExportColumn[] = [
    { key: 'name', label: 'Tên môn học', width: 30 },
    { key: 'description', label: 'Mô tả', width: 40 },
    { key: 'lessonPlanCount', label: 'Số lượng giáo án', width: 15 },
    { key: 'dateCreated', label: 'Ngày tạo', width: 15 }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">QL Môn học</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
        >
          Thêm môn học mới
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            fullWidth
            placeholder="Tìm kiếm theo tên, mô tả..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {canExport && (
            <Tooltip title="Xuất Excel" arrow>
              <IconButton
                color="primary"
                onClick={() => setExportDialogOpen(true)}
                aria-label="Xuất Excel"
                size="small"
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setSearchTerm('');
              loadSubjects();
            }}
          >
            LÀM MỚI
          </Button>
          {canDelete && selectedSubjectIds.length > 0 && (
            <Tooltip title={`Xóa (${selectedSubjectIds.length})`} arrow>
              <IconButton
                color="error"
                onClick={handleBulkDeleteClick}
                aria-label="Xóa"
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ minHeight: 200, width: '100%' }}>
        <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>Tên môn học</TableCell>
              <TableCell sx={{ width: '36%' }}>Mô tả</TableCell>
              <TableCell align="center" sx={{ width: '10%' }}>SL</TableCell>
              <TableCell sx={{ width: '12%' }}>Ngày tạo</TableCell>
              <TableCell align="center" sx={{ width: '15%' }}>Thao tác</TableCell>
              {canDelete && (
                <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                  <Checkbox
                    indeterminate={
                      selectedSubjectIds.length > 0 &&
                      selectedSubjectIds.length < subjects.length
                    }
                    checked={
                      subjects.length > 0 &&
                      selectedSubjectIds.length === subjects.length
                    }
                    onChange={handleSelectAll}
                    size="small"
                    sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                  />
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {subjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canDelete ? 6 : 5} align="center" sx={{ py: 8 }}>
                  <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Typography variant="h6" color="text.secondary">
                      {searchTerm ? 
                        'Không tìm thấy môn học nào phù hợp với điều kiện tìm kiếm' : 
                        'Chưa có môn học nào trong hệ thống'
                      }
                    </Typography>
                    {(searchTerm) && (
                      <Typography variant="body2" color="text.secondary">
                        Thử thay đổi từ khóa tìm kiếm
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              subjects.map((subject) => {
                const lessonPlanCount = subject.lessonPlanCount || 0;
                const isSelected = selectedSubjectIds.includes(subject.id);
                
                logger.log('🔍 SubjectList - Rendering subject:', {
                  id: subject.id,
                  name: subject.name,
                  lessonPlanCount,
                  isSelected,
                  canDelete
                });
                
                return (
                <TableRow key={subject.id}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {subject.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {subject.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="medium">
                      {subject.lessonPlanCount || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(subject.dateCreated)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(subject)}
                      title="Chỉnh sửa"
                      disableFocusRipple
                      disableRipple
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(subject)}
                      title="Xóa"
                      color="error"
                      disableFocusRipple
                      disableRipple
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  {canDelete && (
                    <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectSubject(subject.id, lessonPlanCount);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        size="small"
                        sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                        title="Chọn để xóa"
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bạn có chắc chắn muốn xóa môn học "{subjectToDelete?.name}" không?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Lưu ý: Không thể xóa môn học nếu có giáo án đang sử dụng môn học này. Vui lòng chuyển các giáo án sang môn học khác trước khi xóa.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Hủy</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={handleBulkDeleteCancel}>
        <DialogTitle>Xác nhận xóa nhiều môn học</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bạn có chắc chắn muốn xóa {selectedSubjectIds.length} môn học không?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Lưu ý: Chỉ có thể xóa các môn học có số lượng giáo án = 0. Các môn học có giáo án sẽ không được xóa.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDeleteCancel}>Hủy</Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onExport={handleExportSubjects}
        columns={subjectExportColumns}
        fileName="danh-sach-mon-hoc"
        title="Xuất danh sách môn học"
      />

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Box>
  );
};
